/**
 * sounds.js
 * Tiny Web-Audio feedback sounds (no assets, no network). Lazy singleton
 * AudioContext, fully guarded — silently no-ops where audio is unavailable.
 * Calls always follow a user gesture (qty tap), so autoplay policies allow it.
 */

let audioCtx = null

function getCtx() {
  try {
    if (typeof window === 'undefined') return null
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return null
    if (!audioCtx) audioCtx = new AC()
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {})
    return audioCtx
  } catch {
    return null
  }
}

/**
 * Prime/unlock the audio context from a REAL user gesture (pointerdown /
 * keydown). Browsers only allow an AudioContext to start inside a gesture
 * call stack — but the chime fires from Vue watchers (after the tap's stack
 * is gone), so hosts must call this from a gesture listener first. Idempotent
 * and cheap: safe to call on every tap.
 */
export function primeAudio() {
  getCtx()
}

/**
 * Light, soft two-note chime (E5 → B5) — plays when the user reaches a new
 * promotion step or amount-discount tier, in parallel with the celebration
 * animation and success haptic. Quiet by design (peak gain 0.09).
 */
export function playAchievementChime() {
  try {
    const ac = getCtx()
    if (!ac) return
    const t0 = ac.currentTime
    const notes = [
      [659.25, 0], // E5
      [987.77, 0.09], // B5
    ]
    for (const [freq, dt] of notes) {
      const osc = ac.createOscillator()
      const gain = ac.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, t0 + dt)
      gain.gain.exponentialRampToValueAtTime(0.09, t0 + dt + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.22)
      osc.connect(gain)
      gain.connect(ac.destination)
      osc.start(t0 + dt)
      osc.stop(t0 + dt + 0.25)
    }
  } catch {
    // sound is best-effort feedback — never let it throw into UI code
  }
}
